/**
 * AutoSyncCard — UI dla auto-sync nowych faktur z KSeF do Inbox.
 *
 * Toolbar `/ksef-inbox`: kompaktowa karta z checkboxem + statusem + countdownem.
 *
 * 2026-02-27: PRZEBUDOWA — frontend NIE wykonuje już schedulera. Backendowy
 * runner `services/ksef_auto_sync_background_runner.py` (co 60s) budzi się
 * niezależnie od otwartej karty. Frontend TERAZ pełni tylko role:
 *   - konfiguracja (enabled, frequency_minutes, lookback_days),
 *   - read-only status polling (GET `/settings` co 30s) dla countdown UI,
 *   - odświeżenie inbox przy udanym sync (przez `onNewInvoices` po detekcji
 *     wzrostu `last_sync_at`).
 *
 * Poprzednio: `setInterval(60s)` wywoływał `POST /auto-sync/run` co powodowało
 * że sync działał TYLKO z otwartą kartą.
 */
/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Clock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { logger } from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frontend tick co 60s; jeśli backend zwróci waiting, nic nie robimy (sam się wybudzi).
const POLL_INTERVAL_MS = 60_000;

// Whitelisty zsynchronizowane z backendem (`auto_sync.py`).
const FREQUENCY_OPTIONS = [
  { value: 15, label: "15 minut" },
  { value: 30, label: "30 minut" },
  { value: 60, label: "60 minut" },
  { value: 120, label: "120 minut" },
  { value: 240, label: "240 minut" },
  { value: 1440, label: "1 raz dziennie" },
];

const LOOKBACK_OPTIONS = [
  { value: 0, label: "dzisiaj" },
  { value: 3, label: "ostatnich 3 dni" },
  { value: 7, label: "ostatnich 7 dni" },
  { value: 14, label: "ostatnich 14 dni" },
  { value: 30, label: "ostatnich 30 dni" },
];

const formatFrequencyLabel = (mins) => {
  if (mins === 1440) return "1 dzień";
  if (typeof mins === "number") return `${mins} min`;
  return "—";
};

const formatLookbackLabel = (days) => {
  const opt = LOOKBACK_OPTIONS.find((o) => o.value === days);
  return opt ? opt.label : (typeof days === "number" ? `${days} dni` : "—");
};

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const AutoSyncCard = ({ onNewInvoices }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [running, setRunning] = useState(false);
  const [now, setNow] = useState(Date.now());

  const inFlightRef = useRef(false);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ksef/inbox/auto-sync/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (e) {
      logger.error("AutoSync settings fetch error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Tick 1s dla countdown next_allowed_at
  useEffect(() => {
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const handleToggle = async (checked) => {
    setToggling(true);
    try {
      const res = await axios.post(
        `${API}/ksef/inbox/auto-sync/settings`,
        { enabled: !!checked },
        { withCredentials: true }
      );
      setSettings(res.data);
      toast.success(checked ? "Auto-sync włączony" : "Auto-sync wyłączony");
    } catch (e) {
      logger.error("AutoSync toggle error", e);
      toast.error("Nie udało się zmienić ustawień auto-sync");
    } finally {
      setToggling(false);
    }
  };

  // Zmiana częstotliwości / okna pobierania — wysyła patch do backendu.
  const updateSettings = async (patch) => {
    try {
      const res = await axios.post(
        `${API}/ksef/inbox/auto-sync/settings`,
        patch,
        { withCredentials: true }
      );
      setSettings(res.data);
      toast.success("Zaktualizowano ustawienia auto-sync");
    } catch (e) {
      logger.error("AutoSync settings update error", e);
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Nie udało się zapisać ustawień");
    }
  };

  const runOnce = useCallback(async () => {
    // 2026-02-27: DEPRECATED — zachowana jako no-op dla BC.
    // Backend runner wykonuje wszystkie sync-i niezależnie od UI.
    // Frontend wywoływał tę funkcję z setInterval(60s), co powodowało że
    // scheduler działał tylko z otwartą kartą. Nowy backend runner
    // (`services/ksef_auto_sync_background_runner.py`) budzi się co 60s
    // po stronie serwera.
    return;
  }, []);

  // Polling READ-ONLY co 30s gdy enabled — tylko odświeża status/countdown.
  // NIE wywołuje już `/auto-sync/run` — to jest zadanie backendowego runnera.
  useEffect(() => {
    if (!settings?.enabled) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    const prevSyncAt = settings.last_sync_at;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/ksef/inbox/auto-sync/settings`, { withCredentials: true });
        const next = res.data;
        setSettings(next);
        // Detekcja: backend wykonał sync w tle → odśwież inbox.
        if (next?.last_sync_at && next.last_sync_at !== prevSyncAt) {
          const newCount = (next.last_sync_result || {}).new || 0;
          if (newCount > 0) {
            toast.success(`Auto-sync (backend): ${newCount} nowych faktur`);
            onNewInvoices?.();
          }
        }
      } catch (e) {
        logger.error("AutoSync status poll error", e);
      }
    }, 30_000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [settings?.enabled, settings?.last_sync_at, onNewInvoices]);

  if (loading || !settings) {
    return (
      <Card className="p-3 border-blue-100 bg-blue-50/30" data-testid="auto-sync-card-loading">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Wczytywanie auto-sync…
        </div>
      </Card>
    );
  }

  const enabled = settings.enabled;
  const lastResult = settings.last_sync_result || {};
  let waitSec = 0;
  if (enabled && settings.next_allowed_at) {
    const dt = new Date(settings.next_allowed_at).getTime();
    waitSec = Math.max(0, Math.floor((dt - now) / 1000));
  }

  return (
    <Card
      className={`p-3 ${enabled ? "border-green-300 bg-green-50/40" : "border-gray-200"}`}
      data-testid="auto-sync-card"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        {/* Checkbox + label */}
        <div className="flex items-center gap-2">
          <Checkbox
            checked={enabled}
            disabled={toggling}
            onCheckedChange={handleToggle}
            data-testid="auto-sync-checkbox"
            aria-label="Automatycznie pobieraj nowe faktury z KSeF"
          />
          <Zap className={`w-4 h-4 ${enabled ? "text-green-600" : "text-gray-400"}`} />
          <span className="text-sm font-medium text-gray-800">
            Automatycznie pobieraj nowe faktury z KSeF
          </span>
          {enabled && (
            <Badge variant="outline" className="text-xs bg-green-100 text-green-800 border-green-300">
              {running ? "Synchronizuję…" : "Aktywny"}
            </Badge>
          )}
        </div>

        {/* Status */}
        {enabled && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            {settings.last_sync_at ? (
              <div className="flex items-center gap-1" data-testid="auto-sync-last">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Ostatni: {formatTime(settings.last_sync_at)}</span>
                {typeof lastResult.new === "number" && (
                  <span className="ml-1 font-mono text-gray-700">
                    (<span className="text-green-700 font-semibold">+{lastResult.new}</span>
                    {lastResult.existing > 0 && <span> / {lastResult.existing} ex.</span>})
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1" data-testid="auto-sync-first-run">
                <Clock className="w-3 h-3" />
                <span>Pierwszy sync za chwilę…</span>
              </div>
            )}

            {waitSec > 0 && (
              <div className="flex items-center gap-1 text-amber-700" data-testid="auto-sync-countdown">
                <Clock className="w-3 h-3" />
                <span>Następny za {Math.floor(waitSec / 60)}m {waitSec % 60}s</span>
              </div>
            )}

            {lastResult.errors > 0 && (
              <div className="flex items-center gap-1 text-red-700" data-testid="auto-sync-errors">
                <AlertCircle className="w-3 h-3" />
                <span>{lastResult.errors} błędów</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Konfiguracja automatycznego pobierania — dropdowny + dynamiczny opis */}
      <div className="mt-2 pl-7 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-[12px] text-gray-600 whitespace-nowrap" htmlFor="auto-sync-frequency">
              Częstotliwość:
            </label>
            <Select
              value={String(settings.frequency_minutes)}
              onValueChange={(v) => updateSettings({ frequency_minutes: Number(v) })}
            >
              <SelectTrigger
                id="auto-sync-frequency"
                className="h-7 w-[150px] text-xs"
                data-testid="auto-sync-frequency-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)} data-testid={`auto-sync-frequency-opt-${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-[12px] text-gray-600 whitespace-nowrap" htmlFor="auto-sync-lookback">
              Pobieraj faktury od:
            </label>
            <Select
              value={String(settings.lookback_days)}
              onValueChange={(v) => updateSettings({ lookback_days: Number(v) })}
            >
              <SelectTrigger
                id="auto-sync-lookback"
                className="h-7 w-[170px] text-xs"
                data-testid="auto-sync-lookback-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOOKBACK_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)} data-testid={`auto-sync-lookback-opt-${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {enabled && (
          <div className="text-[11px] text-gray-500" data-testid="auto-sync-summary">
            Częstotliwość: <span className="font-medium text-gray-700">{formatFrequencyLabel(settings.frequency_minutes)}</span>
            {" · "}
            Pobiera od: <span className="font-medium text-gray-700">{formatLookbackLabel(settings.lookback_days)}</span>
            {settings.from_date && (
              <> · <span className="font-mono text-gray-500">(od {settings.from_date})</span></>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default AutoSyncCard;
